import React, { useCallback, useEffect, useRef, useState } from 'react';
import { streamAiTask, aiApi } from '../utils/aiApi';
import renderMarkdown from '../utils/miniMarkdown';
import './AiAssistant.css';

const QUICK_PROMPTS = [
  { icon: '🎨', text: 'How do I change my theme or enable dark mode?' },
  { icon: '📥', text: 'How do Inbox categories like Primary and Promotions work?' },
  { icon: '🗑️', text: 'How do I restore a mail from Trash?' },
  { icon: '🏷️', text: 'How do I create a label and apply it to mail?' },
];

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Floating chatbot panel for ATOZAS AI Assistant.
 * Opened from the draggable FAB — not a full page.
 */
const AiAssistant = ({ currentUser, open = false, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [notice, setNotice] = useState('');
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    aiApi
      .getSettings()
      .then((res) => {
        const d = res?.data;
        if (d && (!d.enabled || !d.configured)) {
          setNotice('AI Assistant is currently unavailable. Please try again later or contact your administrator.');
        }
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => () => abortRef.current?.(), []);

  // Focus input when panel opens
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => textareaRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [open]);

  // Escape closes the panel
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const send = useCallback(
    (text) => {
      const message = String(text ?? input).trim();
      if (!message || streaming) return;

      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const userMsg = { id: uid(), role: 'user', content: message };
      const assistantMsg = { id: uid(), role: 'assistant', content: '' };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setInput('');
      setStreaming(true);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';

      const appendToAssistant = (delta) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantMsg.id ? { ...m, content: m.content + delta } : m))
        );
      };
      const setAssistantError = (msg) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? { ...m, content: m.content || `⚠️ ${msg}`, error: !m.content }
              : m
          )
        );
      };

      abortRef.current = streamAiTask(
        'chat',
        { message, history },
        {
          onDelta: (delta) => appendToAssistant(delta),
          onDone: () => {
            setStreaming(false);
            abortRef.current = null;
          },
          onError: (err) => {
            setAssistantError(err.message || 'AI request failed');
            setStreaming(false);
            abortRef.current = null;
          },
        }
      );
    },
    [input, streaming, messages]
  );

  const handleStop = () => {
    abortRef.current?.();
    abortRef.current = null;
    setStreaming(false);
  };

  const handleNewChat = () => {
    abortRef.current?.();
    abortRef.current = null;
    setStreaming(false);
    setMessages([]);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open) return null;

  const isEmpty = messages.length === 0;
  const firstName = currentUser?.firstName || currentUser?.username || '';

  return (
    <div className="aia-widget-root" role="dialog" aria-label="AI Assistant chatbot">
      <button type="button" className="aia-widget-backdrop" aria-label="Close chatbot" onClick={onClose} />
      <div className="aia-widget" ref={panelRef}>
        <div className="aia-header">
          <div className="aia-header-left">
            <span className="aia-avatar" aria-hidden="true">🤖</span>
            <div>
              <h2 className="aia-title">AI Assistant</h2>
              <p className="aia-subtitle">Ask anything about ATOZASMail</p>
            </div>
          </div>
          <div className="aia-header-actions">
            {!isEmpty && (
              <button type="button" className="aia-icon-btn" onClick={handleNewChat} title="New chat" aria-label="New chat">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            )}
            <button type="button" className="aia-icon-btn" onClick={onClose} title="Close" aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        </div>

        {notice ? <div className="aia-notice">{notice}</div> : null}

        <div className="aia-messages" ref={scrollRef}>
          {isEmpty ? (
            <div className="aia-welcome">
              <div className="aia-welcome-badge">🤖</div>
              <h3>Hi{firstName ? `, ${firstName}` : ''}!</h3>
              <p>Ask how to use ATOZASMail, or get help drafting a message.</p>
              <div className="aia-quick-grid">
                {QUICK_PROMPTS.map((q) => (
                  <button key={q.text} type="button" className="aia-quick" onClick={() => send(q.text)}>
                    <span className="aia-quick-icon">{q.icon}</span>
                    <span>{q.text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`aia-msg aia-msg-${m.role}`}>
                <div className="aia-msg-avatar">{m.role === 'user' ? '🧑' : '🤖'}</div>
                <div className={`aia-bubble ${m.error ? 'is-error' : ''}`}>
                  {m.role === 'assistant' ? (
                    m.content ? (
                      <div
                        className="aia-markdown"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }}
                      />
                    ) : (
                      <span className="aia-typing">
                        <i /><i /><i />
                      </span>
                    )
                  ) : (
                    <div className="aia-user-text">{m.content}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="aia-composer">
          <textarea
            ref={textareaRef}
            className="aia-input"
            placeholder="Type a message…"
            value={input}
            rows={1}
            onChange={(e) => {
              setInput(e.target.value);
              autoResize();
            }}
            onKeyDown={handleKeyDown}
          />
          {streaming ? (
            <button type="button" className="aia-send is-stop" onClick={handleStop} aria-label="Stop">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="aia-send"
              onClick={() => send()}
              disabled={!input.trim()}
              aria-label="Send"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiAssistant;
